import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { LucideIcon } from 'lucide-react'
import type { Project, ProjectSummary, Testimonial } from '@/types'

import Link from 'next/link'

import {
    ArrowRight,
    CalendarDays,
    ChevronRight,
    CircleCheck,
    MapPin,
    Quote,
    Ruler,
    Timer
} from 'lucide-react'

import { buttonVariants } from '@/components/ui/button'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { Eyebrow, SectionHeading } from '@/components/sections/section-heading'
import { CtaBand } from '@/components/sections/cta-band'
import { ProjectCard } from '@/components/projects/project-card'
import { CmsImage } from '@/components/cms/cms-image'
import RichText from '@/components/cms/rich-text'

import {
    REVALIDATE_SECONDS,
    RETRY_SECONDS,
    getProjectBySlug,
    getRelatedProjects,
    getTestimonialForProject,
    loadContent
} from '@/lib/content'

import { contactHref } from '@/constants/site'

interface ProjectPageProps {
    project: Project
    testimonial: Testimonial | null
    related: Array<ProjectSummary>
}

interface ProjectFact {
    label: string
    column: 'location' | 'completed_year' | 'duration' | 'area'
    icon: LucideIcon
}

const facts: Array<ProjectFact> = [
    { label: 'Lokacija', column: 'location', icon: MapPin },
    { label: 'Završeno', column: 'completed_year', icon: CalendarDays },
    { label: 'Trajanje', column: 'duration', icon: Timer },
    { label: 'Površina', column: 'area', icon: Ruler }
]

const ProjectPage: FC<ProjectPageProps> = ({ project, testimonial, related }): ReactNode => {
    const quoteHref = project.service_slug ? `${contactHref}?usluga=${encodeURIComponent(project.service_slug)}` : contactHref
    const shownFacts = facts.filter(fact => project[fact.column])
    const seoTitle = project.location ? `${project.title}, ${project.location}` : project.title

    const gallery = project.gallery
        .map((src, index) => ({ src, alt: project.gallery_alt[index] ?? '', index }))
        .filter(image => image.src.trim().length > 0)

    return (
        <Layout>
            <Seo
                title={seoTitle}
                description={project.summary ?? undefined}
                ogImage={project.cover_image ?? undefined}
            />

            <section id='projekat' className='relative isolate overflow-hidden bg-ink pt-28 pb-36 text-white sm:pt-32 lg:pb-44'>
                <div
                    aria-hidden='true'
                    className='blueprint-grid absolute inset-0 -z-10 text-white [mask-image:radial-gradient(ellipse_at_top_right,black,transparent_70%)]'
                />

                <div
                    aria-hidden='true'
                    className='absolute -top-48 -right-32 -z-10 size-[34rem] rounded-full bg-primary/30 blur-3xl'
                />

                <Container>
                    <nav aria-label='Putanja'>
                        <ol className='flex flex-wrap items-center gap-2 text-sm text-ink-muted'>
                            <li>
                                <Link href='/' className='transition-colors hover:text-white'>
                                    Naslovna
                                </Link>
                            </li>

                            <li aria-hidden='true'>
                                <ChevronRight className='size-3.5' />
                            </li>

                            <li>
                                <Link href='/projekti' className='transition-colors hover:text-white'>
                                    Projekti
                                </Link>
                            </li>

                            <li aria-hidden='true'>
                                <ChevronRight className='size-3.5' />
                            </li>

                            <li aria-current='page' className='text-white'>
                                <span data-mdlfy-cms={`projects:${project.id}:title`}>
                                    {project.title}
                                </span>
                            </li>
                        </ol>
                    </nav>

                    <div className='mt-10 grid gap-10 lg:grid-cols-12 lg:items-end'>
                        <div className='lg:col-span-8'>
                            {project.category ? (
                                <Eyebrow tone='inverse'>
                                    <span data-mdlfy-cms={`projects:${project.id}:category`}>
                                        {project.category}
                                    </span>
                                </Eyebrow>
                            ) : project.service_id && project.service_title && (
                                <Eyebrow tone='inverse'>
                                    <span data-mdlfy-cms={`services:${project.service_id}:title`}>
                                        {project.service_title}
                                    </span>
                                </Eyebrow>
                            )}

                            <h1 className='mt-4 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl lg:leading-[1.05]'>
                                <span data-mdlfy-cms={`projects:${project.id}:title`}>
                                    {project.title}
                                </span>
                            </h1>

                            {project.summary && (
                                <p className='mt-5 max-w-2xl text-lg leading-relaxed text-ink-muted' data-mdlfy-cms={`projects:${project.id}:summary`}>
                                    {project.summary}
                                </p>
                            )}
                        </div>

                        <div className='lg:col-span-4 lg:flex lg:justify-end'>
                            <Link href={quoteHref} className={buttonVariants({ size: 'xl' })}>
                                Zatražite ponudu
                                <ArrowRight data-icon='inline-end' aria-hidden='true' />
                            </Link>
                        </div>
                    </div>

                    {shownFacts.length > 0 && (
                        <dl className='mt-12 grid grid-cols-2 gap-px overflow-hidden rounded-md border border-white/10 bg-white/10 lg:grid-cols-4'>
                            {shownFacts.map(fact => {
                                const Icon = fact.icon

                                return (
                                    <div key={fact.column} className='bg-ink px-5 py-5 sm:px-6'>
                                        <dt className='flex items-center gap-2 text-xs font-bold tracking-[0.14em] text-ink-muted uppercase'>
                                            <Icon className='size-3.5 text-highlight' aria-hidden='true' />
                                            {fact.label}
                                        </dt>

                                        <dd className='mt-2 font-heading text-xl font-bold text-white sm:text-2xl' data-mdlfy-cms={`projects:${project.id}:${fact.column}`}>
                                            {project[fact.column]}
                                        </dd>
                                    </div>
                                )
                            })}
                        </dl>
                    )}
                </Container>
            </section>

            <div className='relative z-10 -mt-24 lg:-mt-32'>
                <Container>
                    <div className='relative aspect-[4/3] overflow-hidden rounded-md bg-muted shadow-2xl sm:aspect-[16/9] lg:aspect-[21/9]'>
                        <CmsImage
                            src={project.cover_image}
                            alt={project.cover_image_alt}
                            sizes='(min-width: 1280px) 1216px, 100vw'
                            preload
                        />
                    </div>
                </Container>
            </div>

            <section id='o-projektu' aria-label='O projektu' className='py-16 lg:py-24'>
                <Container className='grid gap-12 lg:grid-cols-12 lg:gap-16'>
                    <div className='lg:col-span-7'>
                        {project.body ? (
                            <RichText html={project.body} className='text-lg leading-relaxed text-muted-foreground' />
                        ) : (
                            <div>
                                <h2 className='text-2xl font-bold tracking-tight sm:text-3xl'>
                                    O projektu
                                </h2>

                                <p className='mt-4 text-lg leading-relaxed text-muted-foreground'>
                                    Detaljniji opis ovog projekta uskoro dodajemo. Pošaljite nam upit i rado ćemo Vam predstaviti radove koje smo izveli.
                                </p>
                            </div>
                        )}
                    </div>

                    <aside className='lg:col-span-5' aria-label='Detalji projekta'>
                        <div className='space-y-6 lg:sticky lg:top-28'>
                            {project.scope.length > 0 && (
                                <div className='rounded-md border border-border bg-secondary p-7'>
                                    <h2 className='text-xs font-bold tracking-[0.16em] text-foreground uppercase'>
                                        Obim radova
                                    </h2>

                                    <ul className='mt-5 space-y-3.5'>
                                        {project.scope.map(item => (
                                            <li key={item} className='flex items-start gap-3 leading-snug'>
                                                <CircleCheck className='mt-0.5 size-5 shrink-0 text-primary' aria-hidden='true' />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className='relative isolate overflow-hidden rounded-md bg-ink p-7 text-white'>
                                <div
                                    aria-hidden='true'
                                    className='blueprint-grid absolute inset-0 -z-10 text-white [mask-image:linear-gradient(to_bottom_left,black,transparent_70%)]'
                                />

                                <h2 className='text-2xl font-bold tracking-tight'>
                                    Planirate sličan projekat?
                                </h2>

                                <p className='mt-3 leading-relaxed text-ink-muted'>
                                    Pošaljite upit za besplatne konsultacije i procjenu Vaših radova.
                                </p>

                                <Link href={quoteHref} className={buttonVariants({ size: 'lg', className: 'mt-6 w-full' })}>
                                    Pošaljite upit
                                    <ArrowRight data-icon='inline-end' aria-hidden='true' />
                                </Link>
                            </div>
                        </div>
                    </aside>
                </Container>
            </section>

            {testimonial && (
                <section id='iskustvo-klijenta' aria-label='Iskustvo klijenta' className='bg-secondary py-16 lg:py-24'>
                    <Container className='max-w-4xl text-center'>
                        <Quote className='mx-auto size-10 text-primary' aria-hidden='true' />

                        <figure className='mt-6'>
                            <blockquote className='font-heading text-2xl leading-snug font-semibold tracking-tight text-balance sm:text-3xl'>
                                <p data-mdlfy-cms={`testimonials:${testimonial.id}:quote`}>
                                    {testimonial.quote}
                                </p>
                            </blockquote>

                            <figcaption className='mt-8 flex flex-col items-center gap-1'>
                                <span className='font-semibold' data-mdlfy-cms={`testimonials:${testimonial.id}:author_name`}>
                                    {testimonial.author_name}
                                </span>

                                {testimonial.author_detail && (
                                    <span className='text-sm text-muted-foreground' data-mdlfy-cms={`testimonials:${testimonial.id}:author_detail`}>
                                        {testimonial.author_detail}
                                    </span>
                                )}
                            </figcaption>
                        </figure>
                    </Container>
                </section>
            )}

            {gallery.length > 0 && (
                <section id='galerija' aria-labelledby='galerija-title' className='py-16 lg:py-24'>
                    <Container>
                        <SectionHeading align='left' eyebrow='Galerija' title='Još fotografija sa projekta' headingId='galerija-title' />

                        <ul className='mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                            {gallery.map(image => (
                                <li key={`${image.index}-${image.src}`} className='relative aspect-[4/3] overflow-hidden rounded-md bg-muted'>
                                    <CmsImage
                                        src={image.src}
                                        alt={image.alt}
                                        sizes='(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw'
                                    />
                                </li>
                            ))}
                        </ul>
                    </Container>
                </section>
            )}

            {related.length > 0 && (
                <section id='jos-projekata' aria-labelledby='jos-projekata-title' className='border-t border-border bg-secondary py-16 lg:py-24'>
                    <Container>
                        <div className='flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between'>
                            <SectionHeading align='left' eyebrow='Projekti' title='Pogledajte i druge projekte' headingId='jos-projekata-title' />

                            <Link href='/projekti' className={buttonVariants({ size: 'lg', className: 'self-start sm:self-auto' })}>
                                Vidi sve projekte
                                <ArrowRight data-icon='inline-end' aria-hidden='true' />
                            </Link>
                        </div>

                        <div className='mt-12 grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3'>
                            {related.map(item => (
                                <ProjectCard key={item.id} project={item} />
                            ))}
                        </div>
                    </Container>
                </section>
            )}

            <CtaBand />
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: [],
        fallback: 'blocking'
    }
}

export const getStaticProps: GetStaticProps<ProjectPageProps, { slug: string }> = async ({ params }) => {
    const slug = params?.slug

    if (!slug) return { notFound: true, revalidate: REVALIDATE_SECONDS }

    const project = await getProjectBySlug(slug)

    if (!project) return { notFound: true, revalidate: REVALIDATE_SECONDS }

    const [testimonial, related] = await Promise.all([
        loadContent('project testimonial', () => getTestimonialForProject(project.id), null),
        loadContent('related projects', () => getRelatedProjects(project, 3), [])
    ])

    return {
        props: {
            project,
            testimonial: testimonial.data,
            related: related.data
        },
        revalidate: testimonial.failed || related.failed ? RETRY_SECONDS : REVALIDATE_SECONDS
    }
}

export default ProjectPage
